import {Button, FormSubmitHandler, KeyboardShortcut, useDialog} from 'platform/components';
import {Show} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';

import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CheckInHandOverFormType} from '../types/CheckInHandOverFormType';
import {UpdateMileageFuelTankConfirm} from './UpdateMileageFuelTankConfirm';

interface FormActionsProps extends RequiredTestIdProps {
  formApi: UseFormReturn<CheckInHandOverFormType>;
  isCheckin?: boolean;
  isEditing: boolean;
  handleSubmit: FormSubmitHandler<CheckInHandOverFormType>;
  defaultMileage: number | Nullish;
  defaultFuelTank: number | Nullish;
  defaultBatteryCharge: number | Nullish;
}

export function FormActions(props: FormActionsProps) {
  const [isOpen, onOpen, onClose] = useDialog();

  const isInitialCheckIn = props.isCheckin === true && !props.isEditing;

  const handleJustSave = () => {
    props.formApi.setValue('isCompleting', false);
    props.formApi.handleSubmit((formData) =>
      props.handleSubmit(
        {...formData, withFuelTankMileageUpdate: false},
        () => false,
        props.formApi.reset
      )
    )();
  };

  const handleComplete = () => {
    const {mileage, fuelTank, batteryCharge} = props.formApi.getValues();
    const hasVehicleReadingChanged =
      mileage !== props.defaultMileage ||
      fuelTank !== props.defaultFuelTank ||
      batteryCharge !== props.defaultBatteryCharge;

    if (props.isCheckin === true && !isInitialCheckIn && hasVehicleReadingChanged) {
      onOpen();
      return;
    }

    triggerSubmit(isInitialCheckIn && hasVehicleReadingChanged);
  };

  const triggerSubmit = (updateMileageFuelTank: boolean) => {
    onClose();

    props.formApi.setValue('isCompleting', true);
    props.formApi.handleSubmit((formData) =>
      props.handleSubmit(
        {...formData, withFuelTankMileageUpdate: updateMileageFuelTank},
        () => false,
        props.formApi.reset
      )
    )();
  };

  const isDisabled = props.formApi.formState.disabled;

  return (
    <>
      <Show when={!props.isEditing}>
        <Button
          onClick={handleJustSave}
          data-testid={suffixTestId('justSave', props)}
          isLoading={props.formApi.formState.isSubmitting}
          isDisabled={isDisabled}
          variant="secondary"
          title={i18n.t('general.actions.save')}
        />
      </Show>
      <Button
        onClick={handleComplete}
        data-testid={suffixTestId('save', props)}
        isLoading={props.formApi.formState.isSubmitting}
        isDisabled={isDisabled}
        keyboardShortcut={KeyboardShortcut.ModEnter}
        title={
          props.isEditing
            ? i18n.t('general.actions.saveChanges')
            : i18n.t('entity.order.actions.complete')
        }
      />
      <Show when={props.isCheckin === true}>
        <UpdateMileageFuelTankConfirm
          isOpen={isOpen}
          dialogId="UpdateMileageFuelTank"
          onResult={triggerSubmit}
          data-testid={suffixTestId('confirmDialog', props)}
        />
      </Show>
    </>
  );
}
